import { Fragment, PureComponent, ReactNode } from 'react';
import {
    components,
    OptionProps,
    ValueContainerProps,
    ControlProps,
    MenuProps,
    StylesConfig,
    MenuPlacement,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import { CSSObject } from '@emotion/serialize';
import Fuse from 'fuse.js';
import { INVALID_PHONE_NUMBER } from './common/constants/add-patient';
import { getErrorComp } from './PatientPhoneAutoSuggest';

type SuggestionItem = { value: string; label: string };

const Option = (props: OptionProps<SuggestionItem, false>) => {
    const { data } = props;

    return (
        <components.Option {...props}>
            <div className="flex items-center justify-between font-600 text-neutral-3">
                <Fragment>
                    <div className="flex items-center w-75p">
                        <p>{data.value}</p>
                    </div>
                </Fragment>
            </div>
        </components.Option>
    );
};

const ValueContainer = (props: ValueContainerProps<SuggestionItem, false>) => {
    const { hasValue, children, getValue, setValue, selectProps } = props;
    const { isDisabled } = selectProps;
    if (hasValue) {
        const value: SuggestionItem = getValue()[0];

        return (
            <div className="add-pt-input">
                <input
                    type="text"
                    // value={value.personal.phone.n}
                    onChange={() => {
                        setValue(null, 'deselect-option');
                    }}
                    className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                    disabled={isDisabled}
                />
                <div className="flex items-center justify-center w-16 h-16 icon">
                    <svg
                        className="w-16 h-16 fill-neutral-5"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                    >
                        <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z"></path>
                    </svg>
                </div>
                <div className="mimic-box"></div>
            </div>
        );
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const child = children[children.length - 1];

    return child;
};

const Control = (props: ControlProps<SuggestionItem, false>) => {
    const { children } = props;
    return <Fragment>{children}</Fragment>;
};

const Menu = (props: MenuProps<SuggestionItem, false>) => {
    const { children, options } = props;
    if (options.length === 0) {
        return null;
    }

    return <components.Menu {...props}>{children}</components.Menu>;
};

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    const { selectProps, onChange, onFocus, value } = props;
    const { placeholder, onPhoneNumberBlur, onInputChange } = selectProps;

    const onCChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value && value.length > 0 && isNaN(parseInt(value))) {
            return;
        }
        e.target.value = value;
        onChange(e);
    };

    const onPaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
        const v = e.clipboardData.getData('text');
        e.preventDefault();
        e.stopPropagation();

        if (v && v.length >= 10) {
            let t = v.replace(/[^0-9]/g, '').replace('+91', '');
            if (t.length > 10) {
                t = t.substr(t.length - 10);
            }
            if (t.length === 10) {
                onInputChange(t);
            }
        }
    };

    return (
        <div className={`add-pt-input`}>
            <input
                type="text"
                className="focus:outline-none text-14 font-700 placeholder-neutral-6 w-full"
                pattern="\d+"
                placeholder={placeholder}
                value={value}
                onChange={onCChange}
                onFocus={onFocus}
                onBlur={onPhoneNumberBlur}
                onPaste={onPaste}
                maxLength={10}
                minLength={10}
            />{' '}
            <div className="flex items-center justify-center w-16 h-16 icon">
                <svg
                    className="w-16 h-16 fill-neutral-5"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M511.2 387l-23.25 100.8c-3.266 14.25-15.79 24.22-30.46 24.22C205.2 512 0 306.8 0 54.5c0-14.66 9.969-27.2 24.22-30.45l100.8-23.25C139.7-2.602 154.7 5.018 160.8 18.92l46.52 108.5c5.438 12.78 1.77 27.67-8.98 36.45L144.5 207.1c33.98 69.22 90.26 125.5 159.5 159.5l44.08-53.8c8.688-10.78 23.69-14.51 36.47-8.975l108.5 46.51C506.1 357.2 514.6 372.4 511.2 387z"></path>
                </svg>
            </div>
            <div className="mimic-box"></div>
        </div>
    );
};

const DropdownIndicator = () => null;
const NoOptionsMessage = () => null;
const LoadingIndicator = () => null;
const ClearIndicator = () => null;
const IndicatorsContainer = () => null;

const customComponents = {
    DropdownIndicator,
    ValueContainer,
    Input,
    Control,
    Option,
    NoOptionsMessage,
    LoadingIndicator,
    Menu,
    ClearIndicator,
    IndicatorsContainer,
};

const customStyles: StylesConfig<SuggestionItem, false> = {
    menu: (base) => {
        return {
            ...base,
            paddingTop: 0,
        } as CSSObject;
    },
    option: (base, state) =>
        ({
            ...base,
            backgroundColor: state.isFocused ? '#f4f6f9' : 'white',
        } as CSSObject),
};

type Props = {
    inputValue: string;
    onSelect: (suggestion?: SuggestionItem) => void;
    onInputChange: (text: string) => void;
    phoneNumbers: number[];
    isPhoneNumberError?: boolean;
    className?: string;
    menuPlacement?: MenuPlacement;
};

type State = {
    menuIsOpen: boolean;
};

const fuseConfig = { keys: ['value'], threshold: 0.2 };

class PatientPhoneNumber extends PureComponent<Props, State> {
    fuse = new Fuse<SuggestionItem>([], fuseConfig);

    state: State = {
        menuIsOpen: false,
    };

    componentDidMount(): void {
        this.fuse = new Fuse<SuggestionItem>(
            this.props?.phoneNumbers?.map((num) => ({ value: `${num}`, label: `${num}` })) || [],
            fuseConfig,
        );
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (prevProps?.phoneNumbers?.length !== this.props?.phoneNumbers?.length) {
            this.fuse = new Fuse<SuggestionItem>(
                this.props?.phoneNumbers?.map((num) => ({ value: `${num}`, label: `${num}` })) ||
                    [],
                fuseConfig,
            );
        }
    }

    onPhoneNumberChange = (input: string): void => {
        this.setState({
            menuIsOpen: true,
        });

        this.props.onInputChange(input);
    };

    onChange = (suggestion: SuggestionItem | null): void => {
        const { onSelect } = this.props;

        if (suggestion === null) {
            onSelect();
            return;
        }

        onSelect(suggestion);
    };

    loadPhoneNumber = async (phone: string): Promise<SuggestionItem[]> => {
        const data = this.fuse.search(phone).map((option) => option.item);
        return data;
    };

    private readonly filterOption = () => true;

    onPhoneNumberBlur = (): void => {
        this.setState({ menuIsOpen: false });
    };

    render(): ReactNode {
        const { menuIsOpen } = this.state;
        const { inputValue, isPhoneNumberError, className, menuPlacement } = this.props;

        const children = (
            <AsyncSelect
                value={[]}
                isClearable={true}
                components={customComponents}
                menuPlacement={menuPlacement || 'bottom'}
                styles={customStyles}
                loadOptions={this.loadPhoneNumber}
                onInputChange={this.onPhoneNumberChange}
                inputValue={inputValue}
                onChange={this.onChange}
                onPhoneNumberBlur={this.onPhoneNumberBlur}
                filterOption={this.filterOption}
                menuIsOpen={menuIsOpen}
                tabSelectsValue={false}
                placeholder={"Referred By Doctor's Number"}
                className={className || 'w-50p'}
            />
        );

        return (
            <div className={`space-y-4`}>
                <div className="flex justify-between">
                    <p className="text-neutral-3 text-14 font-700">Referred By Doctor's Number</p>
                </div>
                {children}
                {isPhoneNumberError && (
                    <div className={`mt-4 ${className || 'w-50p'}`}>
                        {getErrorComp(INVALID_PHONE_NUMBER)}
                    </div>
                )}
            </div>
        );
    }
}

export default PatientPhoneNumber;
